// THE switch for the sidebar (#141): Server.tsx asks for the model of the layout in use and draws what it returns.
// Chats: the list of every chat (components/chats/). Classic: the channels by category, banner and unread pills
// (components/classic/). IRC (#154): the switchbar and the window tree (components/irc/). Removing a layout is: delete its folder, its branch below, and (for Chats) chats.ts and
// chatsLogic.ts. Everything else the two share (sockets, unread state, menus, the composer) stays out of both.
import type { ReactNode } from "react";
import type { Category, Channel, ChatLayout, MyChats } from "@bloom/shared";
import { dmHidden } from "@bloom/shared";
import { useChatRows } from "./chats.ts";
import { ChatsSidebar } from "./components/chats/ChatsSidebar.tsx";
import { ClassicSidebar } from "./components/classic/ClassicSidebar.tsx";
import { flatChannels, knownChannels, lastTimes, useByActivity, useFlatList, useGroupIdle } from "./components/classic/listOptions.ts";
import { IrcSidebar, IrcStrip } from "./components/irc/IrcNav.tsx";
import { shownDms } from "./components/DmList.tsx";
import type { SidebarProps } from "./components/SidebarProps.ts";
import type { ChannelState } from "./realtime.ts";
import { isUnread, sidebarOrder } from "./readState.ts";

export interface SidebarInput {
  serverId: string;
  me: string | null;
  /** The open server's people by id. */
  names: Map<string, string>;
  channels: Channel[];
  categories: Category[];
  byChannel: Record<string, ChannelState>;
  read: Record<string, number>;
  mentionCounts: Record<string, number>;
  dmClosed: Record<string, number>;
  muted: Set<string>;
  activeId: string | undefined;
  myChats?: MyChats | null;
}

export interface SidebarModel {
  /** The sidebar, given what both layouts get. */
  render: (props: Omit<SidebarProps, "names">) => ReactNode;
  /**
   * Optional (IRC, #154): a bar of windows drawn outside the sidebar, on desktop across the top of the page and on
   * phones under the header; `variant` says which. Layouts without one leave it out.
   */
  strip?: (props: Omit<SidebarProps, "names">, variant: "desktop" | "phone") => ReactNode;
  /** What the phone's menu button opens: "chats", "channels" or "windows". */
  noun: string;
  /** Something unread besides the open conversation (the menu button's dot). */
  anyUnread: boolean;
  /** The order Alt+↑/↓ steps through. */
  order: Channel[];
}

export function useSidebarModel(layout: ChatLayout, input: SidebarInput): SidebarModel {
  const { serverId, me, names, channels, categories, byChannel, read, mentionCounts, dmClosed, muted, activeId, myChats } = input;
  // #297: the Classic list sorts by the newest message only while flat and by activity are both on; then it reads the same rows.
  const flat = useFlatList();
  const byActivity = useByActivity() && flat;
  // #601: Group idle channels judges each channel on its newest message time, so it reads the same rows.
  const groupIdle = useGroupIdle();
  // Always called (hooks can't change with the layout); it fetches and polls only for Chats (the Classic list reads the poll Server.tsx holds).
  const chats = useChatRows(layout === "chats" || (layout === "classic" && (byActivity || groupIdle)), serverId, { me, channels, byChannel, read, mentionCounts, dmClosed, muted, activeId }, names, myChats);
  if (layout === "classic") {
    const rows = byActivity || groupIdle ? chats.rows.filter((r) => r.serverId === serverId) : [];
    const times = lastTimes(rows);
    const known = knownChannels(rows);
    const dms = shownDms(channels.filter((c) => c.kind === "dm"), byChannel, activeId, dmClosed);
    return {
      render: (props) => <ClassicSidebar {...props} names={names} lastTimes={times} knownChannels={known} />,
      noun: "channels",
      anyUnread: channels.some((c) => c.id !== activeId && isUnread(c, { read, me }) && !muted.has(c.id) && !(c.kind === "dm" && dmHidden(c.lastSeq, dmClosed[c.id], false))),
      // Flat: the order of the flat list, for Alt+↑/↓. The quick switcher (Server.tsx `switcherItems`) still groups by category.
      order: flat ? [...dms, ...flatChannels(channels, byActivity, (id) => times.get(id))] : sidebarOrder(channels, categories, dms),
    };
  }
  if (layout === "irc") {
    return {
      render: (props) => <IrcSidebar {...props} names={names} />,
      strip: (props, variant) => <IrcStrip {...props} names={names} variant={variant} />,
      noun: "windows",
      anyUnread: channels.some((c) => c.id !== activeId && isUnread(c, { read, me }) && !muted.has(c.id) && !(c.kind === "dm" && dmHidden(c.lastSeq, dmClosed[c.id], false))),
      // The switchbar's order: channels by category, then the DMs.
      order: [...sidebarOrder(channels, categories, []), ...shownDms(channels.filter((c) => c.kind === "dm"), byChannel, activeId, dmClosed)],
    };
  }
  return {
    render: (props) => <ChatsSidebar {...props} names={names} chats={chats.rows} chatNames={chats.names} />,
    noun: "chats",
    anyUnread: chats.rows.some((c) => c.unread && !(c.serverId === serverId && c.channelId === activeId)),
    // The order of the list: this server's chats, newest first.
    order: chats.rows.filter((r) => r.serverId === serverId && !r.closed).flatMap((r) => channels.find((c) => c.id === r.channelId) ?? []),
  };
}
